import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Search, Download, ShieldCheck, BadgeCheck, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, hint, color = "slate" }) => (
  <div className={`bg-white border border-${color}-200 rounded-xl p-4`}>
    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
    <p className={`mt-2 text-3xl font-bold text-${color}-900`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const AdminUsers = ({ adminKey }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, verified: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const client = useMemo(() => axios.create({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  useEffect(() => {
    setLoading(true);
    client.get(`${API}/admin/users`)
      .then((r) => { setUsers(r.data.users); setStats({ total: r.data.total, paid: r.data.paid, verified: r.data.verified }); })
      .catch((err) => { console.error("Failed to load users:", err); toast.error("Failed to load users"); })
      .finally(() => setLoading(false));
  }, [client]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter === "paid" && !u.is_paid) return false;
      if (filter === "unverified" && u.email_verified) return false;
      if (filter === "suspended" && !u.suspended) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.handle || "").toLowerCase().includes(q);
    });
  }, [users, search, filter]);

  const exportCsv = () => {
    const header = ["name", "email", "handle", "email_verified", "is_paid", "subscription_status", "clicks_this_month", "total_earned", "suspended", "created_at"];
    const rows = filtered.map((u) => header.map((k) => JSON.stringify(u[k] ?? "")).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `social-flats-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} users`);
  };

  return (
    <section data-testid="admin-users-section" className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total users" value={stats.total} hint="All signed-up accounts" />
        <StatCard label="Paid members" value={stats.paid} hint="$1.99/mo active" color="emerald" />
        <StatCard label="Email verified" value={stats.verified} hint={`${Math.round((stats.verified / Math.max(1, stats.total)) * 100)}% of total`} color="indigo" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <Users className="w-5 h-5" /> All users
          </div>
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              data-testid="users-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, or handle…"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            data-testid="users-filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All</option>
            <option value="paid">Paid only</option>
            <option value="unverified">Unverified</option>
            <option value="suspended">Suspended</option>
          </select>
          <button
            data-testid="users-export-csv-button"
            onClick={exportCsv}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-700"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">No users match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 px-2 text-left">Name</th>
                  <th className="py-2 px-2 text-left">Email</th>
                  <th className="py-2 px-2 text-left">Handle</th>
                  <th className="py-2 px-2 text-center">Verified</th>
                  <th className="py-2 px-2 text-center">Membership</th>
                  <th className="py-2 px-2 text-right">Clicks / mo</th>
                  <th className="py-2 px-2 text-right">Earned</th>
                  <th className="py-2 px-2 text-left">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${u.suspended ? "opacity-50" : ""}`}>
                    <td className="py-2.5 px-2 font-medium text-slate-900">{u.name}</td>
                    <td className="py-2.5 px-2 text-slate-600">{u.email}</td>
                    <td className="py-2.5 px-2 text-slate-500 font-mono text-xs">@{u.handle}</td>
                    <td className="py-2.5 px-2 text-center">
                      {u.email_verified
                        ? <BadgeCheck className="w-5 h-5 text-emerald-600 inline" />
                        : <XCircle className="w-5 h-5 text-slate-300 inline" />}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      {u.is_paid
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200"><ShieldCheck className="w-3 h-3" /> Paid</span>
                        : <span className="text-slate-400 text-xs">Free</span>}
                    </td>
                    <td className="py-2.5 px-2 text-right text-slate-700">{u.clicks_this_month || 0}</td>
                    <td className="py-2.5 px-2 text-right text-slate-700">${(u.total_earned || 0).toFixed(2)}</td>
                    <td className="py-2.5 px-2 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
